import React from "react";
//#region types
import { FieldConstants } from "utils/constants";
//#endregion
//#region components
import { DragSourceMonitor, DropTargetMonitor, useDrag, useDrop } from "react-dnd";
import type { Identifier } from "dnd-core";
//#endregion

function useDraggableRow(
  id: number,
  rowIndex: number,
  isDragDisabled: boolean,
  moveRow: (dragIndex: any, hoverIndex: any) => any,
  hoverRow: (dragIndex: any, hoverIndex: number) => any
) {
  const dropRef = React.useRef<HTMLTableRowElement>(null);

  const [{ handlerId }, drop] = useDrop<any, void, { handlerId: Identifier | null }>(
    {
      accept: FieldConstants.DndItemType,
      collect: (monitor: DropTargetMonitor) => {
        return {
          handlerId: monitor.getHandlerId(),
        };
      },
      hover: async (item: any) => {
        if (!dropRef.current) {
          return;
        }

        const hoverIndex = rowIndex;
        const currentIndex = item.currentIndex;

        if (currentIndex === hoverIndex) {
          return;
        }

        hoverRow(item.originalIndex, hoverIndex);
        item.currentIndex = hoverIndex;
      },
    },
    [rowIndex, hoverRow]
  );

  const [{ isDragging }, drag] = useDrag(
    {
      type: FieldConstants.DndItemType,
      item: () => {
        if (isDragDisabled) {
          return;
        }
        return { id, originalIndex: rowIndex, currentIndex: rowIndex };
      },
      collect: (monitor: DragSourceMonitor) => ({
        isDragging: monitor.isDragging(),
      }),
      end: (item, monitor) => {
        const didDrop = monitor.didDrop();
        if (item) {
          if (didDrop) {
            moveRow(item.originalIndex, item.currentIndex);
          } else {
            hoverRow(item.originalIndex, item.originalIndex);
          }
        }
      },
      canDrag: !isDragDisabled,
    },
    [id, rowIndex, moveRow]
  );

  drag(drop(dropRef));

  return { dropRef, isDragging, handlerId };
}

export default useDraggableRow;
